import React, { useEffect, useMemo, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Radio } from "iconsax-react";
import BroadcastSidebar from "../../components/Broadcasts/BroadcastSidebar";
import BroadcastNavbar from "../../components/Broadcasts/BroadcastNavbar";
import BroadcastTable from "../../components/Broadcasts/BroadcastTable";
import AddBroadcastModal from "../../components/Broadcasts/AddBroadcastModal";
import PanelHeading from "../../components/Navbar/PanelHeading";
import {
  fetchBroadcasts,
  resendBroadcast,
  cancelBroadcast,
  deleteBroadcast,
  BROADCAST_STATUS_TO_BACKEND,
} from "../../redux/Slice/Broadcasts/broadcastsSlice";

function BroadcastsPage() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountid } = useParams();
  const { campaigns, pagination, loading } = useSelector((state) => state.broadcasts);

  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [addBroadcastOpen, setAddBroadcastOpen] = useState(false);

  useEffect(() => {
    dispatch(
      fetchBroadcasts({
        status: BROADCAST_STATUS_TO_BACKEND[selectedStatus],
        page: page + 1,
        limit: rowsPerPage,
      })
    );
  }, [dispatch, selectedStatus, page, rowsPerPage]);

  const handleStatusSelect = (status) => {
    setSelectedStatus(status);
    setPage(0);
  };

  const handlePageChange = (_, newPage) => setPage(newPage);

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResend = async (campaignId) => {
    try {
      await dispatch(resendBroadcast({ campaignId })).unwrap();
      toast.success("Resending failed messages.");
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Failed to resend broadcast.");
    }
  };

  const handleCancel = async (campaignId) => {
    try {
      await dispatch(cancelBroadcast({ campaignId })).unwrap();
      toast.success("Broadcast moved to draft.");
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Failed to cancel broadcast.");
    }
  };

  const handleDelete = async (campaignId) => {
    try {
      await dispatch(deleteBroadcast({ campaignId })).unwrap();
      toast.success("Broadcast deleted.");
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Failed to delete broadcast.");
    }
  };

  const handleCreateBroadcast = ({ name, label }) => {
    navigate(`/home/${accountid}/contact-groups/new`, { state: { name, label } });
  };

  const handleOpenBroadcast = (campaign) => {
    navigate(`/home/${accountid}/contact-groups/new`, { state: { campaign } });
  };

  const visibleCampaigns = useMemo(() => {
    if (!searchValue.trim()) return campaigns;
    const term = searchValue.trim().toLowerCase();
    return campaigns.filter((c) => c.campaignName?.toLowerCase().includes(term));
  }, [campaigns, searchValue]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "row",
        overflow: "hidden",
      }}
    >
      <BroadcastSidebar
        selectedStatus={selectedStatus}
        onSelectStatus={handleStatusSelect}
      />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Page-title header, matching Settings'/Reports' content-pane header
            exactly -- BroadcastNavbar below is unchanged, it was always just
            a search+create toolbar, never a title. */}
        <PanelHeading
          title="Broadcasts"
          subtitle="Send and track bulk messages to your contacts."
          icon={<Radio size="100%" color={theme.palette.text.secondary} variant="Linear" />}
          variant="full"
        />
        <BroadcastNavbar
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onAddBroadcast={() => setAddBroadcastOpen(true)}
        />
        <BroadcastTable
          campaigns={visibleCampaigns}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={pagination.totalCampaigns}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onResend={handleResend}
          onCancel={handleCancel}
          onDelete={handleDelete}
          onOpenBroadcast={handleOpenBroadcast}
        />
      </Box>

      <AddBroadcastModal
        open={addBroadcastOpen}
        onClose={() => setAddBroadcastOpen(false)}
        onCreate={handleCreateBroadcast}
      />
    </Box>
  );
}

export default BroadcastsPage;
